import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Col, Form, Modal, Row } from 'react-bootstrap';
import { GeoAreaTypes } from '@awp/constants';
import AwpDropdown from '@components/Common/DropDown/AwpDropdown';
import { selectContractAreas, selectEetAreas, selectSensorTypes } from '@stores/dropdownsSlice';
import { clearData, clearWeatherStation } from '@stores/dataVisualizationSlice';
import {
  selectAreaTypeName,
  selectContractArea,
  selectEetArea,
  selectSensorType,
  selectWeatherstationStatus,
  selectWeatherstationStatuses,
  setAllFilteredWeatherStations,
  setAreaTypeName,
  setContractArea,
  setEetArea,
  setSensorType,
  setWeatherstationStatus,
  setWeatherStationId,
} from '@stores/weatherStationSlice';
import { Crud } from '@awp/shared/Crud';

const SelectEnum = {
  WeatherStationStatus: 1,
  SensorType: 2,
  ContractArea: 3,
  EETArea: 4,
  AreaType: 5,
};

const WeatherStationFilterModal = (props) => {
  const { sensorTypeDisabled = false, ...rest } = props;

  const dispatch = useDispatch();
  const allContractOptions = useSelector(selectContractAreas);
  const allEETOptions = useSelector(selectEetAreas);
  const allWeatherstationStatuses = useSelector(selectWeatherstationStatuses);
  const allSensorTypes = useSelector(selectSensorTypes);
  const currentAreaTypeName = useSelector(selectAreaTypeName);
  const currentEETArea = useSelector(selectEetArea);
  const currentContractArea = useSelector(selectContractArea);
  const currentSensorType = useSelector(selectSensorType);
  const currentWeatherstationStatus = useSelector(selectWeatherstationStatus);

  const [weatherStation, setWeatherStation] = useState([]);
  const [sensor, setSensor] = useState([]);
  const [contract, setContract] = useState([]);
  const [eet, setEet] = useState([]);
  const [area, setArea] = useState([]);

  const crud = new Crud();

  useEffect(() => {
    setArea(currentAreaTypeName);
    setEet(currentEETArea);
    setContract(currentContractArea);
    setSensor(currentSensorType);
    setWeatherStation(currentWeatherstationStatus);
  }, [currentAreaTypeName, currentEETArea, currentContractArea, currentSensorType, currentWeatherstationStatus]);

  const allAreaTypesOptions = [
    { value: GeoAreaTypes.CONTRACT, label: GeoAreaTypes.CONTRACT_DISPLAY },
    { value: GeoAreaTypes.EET, label: GeoAreaTypes.EET_DISPLAY },
  ];

  const handleChange = (e, selectIdentifier) => {
    switch (selectIdentifier) {
      case SelectEnum.WeatherStationStatus:
        setWeatherStation(e);
        break;
      case SelectEnum.SensorType:
        setSensor(e);
        break;
      case SelectEnum.ContractArea:
        setContract(e);
        break;
      case SelectEnum.EETArea:
        setEet(e);
        break;
      case SelectEnum.AreaType:
        setArea(e);
        break;
    }
  };

  const applyFilter = () => {
    dispatch(setWeatherStationId(null));
    dispatch(setAreaTypeName(area));
    dispatch(setContractArea(contract));
    dispatch(setEetArea(eet));
    dispatch(setSensorType(sensor));
    dispatch(setWeatherstationStatus(weatherStation));

    dispatch(setAllFilteredWeatherStations());
    dispatch(clearWeatherStation());
    dispatch(clearData());

    if (props.onHide && props.onHide instanceof Function) props.onHide();
  };

  const resetFilter = () => {
    setArea(currentAreaTypeName);
    setContract(currentContractArea);
    setEet(currentEETArea);
    setSensor(currentSensorType);
    setWeatherStation(currentWeatherstationStatus);

    if (props.onHide && props.onHide instanceof Function) props.onHide();
  };

  return (
    <Modal
      {...rest}
      onHide={resetFilter}
      aria-labelledby="contained-modal-title-vcenter"
      size={'lg'}
      backdrop="static"
      keyboard={false}
    >
      <Modal.Header closeButton>
        {crud.getModalTitle({ title: 'Weather Station Filter', id: 'contained-modal-title-vcenter' })}
      </Modal.Header>
      <Modal.Body className="grid-example">
        <Row>
          <Col sm={6} style={{ zIndex: 14 }} className="mt-2">
            <AwpDropdown
              isMulti={true}
              selectedValues={weatherStation}
              onChange={(e) => handleChange(e, SelectEnum.WeatherStationStatus)}
              showCountOnly={false}
              hideSelectedOptions={true}
              allowAll={true}
              data={{
                options: allWeatherstationStatuses,
                status: 'succeeded',
              }}
              title={'Status'}
            />
          </Col>
          <Col sm={6} style={{ zIndex: 13 }} className="mt-2">
            <AwpDropdown
              isMulti={false}
              selectedValues={area}
              onChange={(e) => handleChange(e, SelectEnum.AreaType)}
              showCountOnly={false}
              //hideSelectedOptions={true}
              allowAll={false}
              data={{
                options: allAreaTypesOptions,
                status: 'succeeded',
              }}
              title={'Geographic Area Type'}
            />
          </Col>
        </Row>
        <Row>
          <Col sm={6} style={{ zIndex: 12 }} className="mt-2">
            {sensorTypeDisabled ? (
              <>
                <Form.Label>
                  <b>Sensor Type</b>
                </Form.Label>
                <Form.Control id="sensorType" type="text" readOnly={sensorTypeDisabled} value={sensor?.name} />
              </>
            ) : (
              <AwpDropdown
                isMulti={false}
                selectedValues={sensor}
                onChange={(e) => handleChange(e, SelectEnum.SensorType)}
                allowAll={true}
                data={{
                  options: allSensorTypes.options,
                  status: 'succeeded',
                }}
                title={'Sensor Type'}
              />
            )}
          </Col>
          <Col sm={6} style={{ zIndex: 11 }} className="mt-2">
            {area && area.value === GeoAreaTypes.EET && (
              <AwpDropdown
                isMulti={true}
                selectedValues={eet}
                onChange={(e) => handleChange(e, SelectEnum.EETArea)}
                showCountOnly={false}
                hideSelectedOptions={true}
                allowAll={true}
                data={{
                  options: allEETOptions,
                  status: 'succeeded',
                }}
                title={GeoAreaTypes.EET_DISPLAY}
              />
            )}
            {area && area.value === GeoAreaTypes.CONTRACT && (
              <AwpDropdown
                isMulti={true}
                selectedValues={contract}
                onChange={(e) => handleChange(e, SelectEnum.ContractArea)}
                showCountOnly={false}
                hideSelectedOptions={true}
                allowAll={true}
                data={{
                  options: allContractOptions,
                  status: 'succeeded',
                }}
                title={GeoAreaTypes.CONTRACT_DISPLAY}
              />
            )}
          </Col>
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" onClick={applyFilter}>
          Apply
        </Button>
        <Button variant="secondary" onClick={resetFilter}>
          Cancel
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default WeatherStationFilterModal;
